import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { MessageModule } from "primeng/message";
import { MessagesModule } from "primeng/messages";
import { ProgressSpinnerModule } from "primeng/progressspinner";

// Our interfaces
import { ConfirmationDialogData } from "../../interfaces/confirmation-dialog-data.interface";

/**
 * Advanced Confirmation Dialog Component
 * Enhanced confirmation dialog with context-aware messages and custom styling
 * Matches old QMC PromptDialogComponent functionality with modern PrimeNG
 */
@Component({
  selector: "app-advanced-confirmation-dialog",
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    MessageModule,
    MessagesModule,
    ProgressSpinnerModule,
  ],
  templateUrl: "./advanced-confirmation-dialog.component.html",
  styleUrl: "./advanced-confirmation-dialog.component.scss",
})
export class AdvancedConfirmationDialogComponent implements OnInit {
  data: ConfirmationDialogData;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig<ConfirmationDialogData>,
  ) {
    this.data = this.config.data!;
  }

  ngOnInit(): void {
    // Set default values if not provided
    if (!this.data.okButtonText) {
      this.data.okButtonText = "Confirm";
    }
    if (!this.data.cancelButtonText) {
      this.data.cancelButtonText = "Cancel";
    }
    if (this.data.showCancelButton === undefined) {
      this.data.showCancelButton = true;
    }
    if (!this.data.severity) {
      this.data.severity = "warning";
    }
    if (!this.data.icon) {
      this.data.icon = this.getDefaultIcon();
    }
  }

  /**
   * Handle OK/Confirm button click
   */
  onConfirm(): void {
    this.ref.close(true);
  }

  /**
   * Handle Cancel button click
   */
  onCancel(): void {
    this.ref.close(false);
  }

  /**
   * Get default icon based on severity
   */
  private getDefaultIcon(): string {
    switch (this.data.severity) {
      case "danger":
        return "pi pi-exclamation-triangle";
      case "warning":
        return "pi pi-exclamation-circle";
      case "success":
        return "pi pi-check-circle";
      case "info":
      default:
        return "pi pi-info-circle";
    }
  }

  /**
   * Get severity color class
   */
  getSeverityClass(): string {
    switch (this.data.severity) {
      case "danger":
        return "text-red-600";
      case "warning":
        return "text-orange-600";
      case "success":
        return "text-green-600";
      case "info":
      default:
        return "text-blue-600";
    }
  }

  /**
   * Get button severity for confirm button
   */
  getConfirmButtonSeverity():
    | "primary"
    | "danger"
    | "success"
    | "info"
    | "help"
    | "secondary"
    | "contrast"
    | undefined {
    switch (this.data.severity) {
      case "danger":
        return "danger";
      case "warning":
        return "primary"; // PrimeNG doesn't have warning severity for buttons
      case "success":
        return "success";
      case "info":
      default:
        return "primary";
    }
  }
}
